import ClientLayout from "../../components/Layout/ClientLayout";
import AboutUsImage from "../../assets/images/AboutUs.jpg";
import Avatar from "../../assets/images/avatar.jpg";
import Avatar2 from "../../assets/images/Avatar2.png";
import Avatar3 from "../../assets/images/Avatar3.png";

import { IoLogoInstagram } from "react-icons/io";
import { FaFacebookF, FaTwitter } from "react-icons/fa";
import { motion } from "framer-motion";
import { Mountain, Users, Target, Award, Mail, MapPin, Quote, Sparkles } from "lucide-react";

const AboutUs = () => {
  const handleSendEmail = () => {
    const to = "info@mambahikers.com";
    const subject = encodeURIComponent("Hello from My App");
    const body = encodeURIComponent(
      "Hi there,\n\nI wanted to reach out to you..."
    );

    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${to}`;

    window.open(gmailUrl, "_blank");
  };
  return (
    <ClientLayout>
      <div>
        {/* HERO COMPONENT */}
        <div className="relative h-[500px] lg:h-[600px] bg-about-us bg-cover bg-center overflow-hidden">
          {/* Animated Gradient Overlay */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 bg-gradient-to-br from-mamba-navy/95 via-slate-900/90 to-mamba-sky-blue/80"
          />
          
          {/* Floating Mountain Icons */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-10">
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute"
                style={{
                  left: `${Math.random() * 90}%`,
                  top: `${Math.random() * 90}%`,
                }}
                animate={{
                  y: [0, -30, 0],
                  rotate: [0, 15, 0],
                }}
                transition={{
                  duration: 4 + i,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <Mountain className="w-16 h-16 text-white" />
              </motion.div>
            ))}
          </div>

          {/* Content */}
          <div className="relative z-10 h-full flex flex-col justify-center items-center text-center px-5">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-mamba-golden/20 backdrop-blur-sm rounded-full border border-mamba-golden/40 mb-6"
            >
              <Sparkles className="w-4 h-4 text-mamba-golden" />
              <span className="text-sm font-bold font-montserrat text-mamba-golden uppercase tracking-wider">Who We Are</span>
            </motion.div>

            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-5xl md:text-6xl lg:text-7xl font-black font-montserrat text-white mb-6 leading-tight"
            >
              About <span className="italic font-dancing-script text-mamba-golden">Mamba</span> Hikers
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-lg md:text-xl text-white/90 font-inter max-w-3xl"
            >
              Conquering peaks, conquering yourself. Join a tribe united by adventure.
            </motion.p>

            {/* Scroll Indicator */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, y: [0, 10, 0] }}
              transition={{ 
                opacity: { delay: 1, duration: 0.6 },
                y: { duration: 2, repeat: Infinity, ease: "easeInOut" }
              }}
              className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
            >
              <div className="w-6 h-10 rounded-full border-2 border-white/50 flex items-start justify-center p-2">
                <motion.div 
                  animate={{ y: [0, 12, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="w-1.5 h-1.5 bg-white rounded-full"
                />
              </div>
            </motion.div>
          </div>
        </div>

        {/* Our Story Section */}
        <section className="px-5 md:px-10 lg:px-20 xl:px-32 py-16 lg:py-24 bg-gradient-to-br from-white via-mamba-bg-light to-white">
          <div className="max-w-8xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Left: Content */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                {/* Section Header */}
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-1 w-12 bg-gradient-to-r from-mamba-sky-blue to-transparent rounded-full" />
                    <span className="text-xs font-bold font-montserrat uppercase tracking-wider text-mamba-sky-blue">
                      Who We Are
                    </span>
                  </div>
                  <h2 className="text-4xl md:text-5xl lg:text-6xl font-black font-montserrat text-mamba-navy leading-tight">
                    Our <span className="italic font-dancing-script bg-gradient-to-r from-mamba-sky-blue to-mamba-golden bg-clip-text text-transparent">Story</span>
                  </h2>
                </div>

                {/* Story Content */}
                <div className="space-y-6">
                  <p className="text-lg md:text-xl font-inter text-slate-700 leading-relaxed">
                    At Mamba Hikers, we're not just about conquering peaks; we're
                    about <span className="font-bold text-mamba-navy">conquering yourself</span>. Every summit we reach is a step
                    towards rediscovering the wild spirit within. We're a tribe of
                    adventure seekers, united by the rhythm of boots on dirt and the
                    thrill of pushing beyond our limits.
                  </p>

                  <p className="text-lg md:text-xl font-inter text-slate-700 leading-relaxed">
                    Our journey began with a simple yet powerful belief that every
                    individual, regardless of their hiking experience, possesses an
                    innate ability to conquer the most challenging peak. Mamba
                    Hikers is more than a brand; it's a <span className="font-bold text-mamba-sky-blue">movement</span> that invites
                    everyone, from seasoned trekkers to first-time explorers, to
                    embrace the thrill of the trail.
                  </p>

                  <p className="text-lg md:text-xl font-inter text-slate-700 leading-relaxed">
                    Discover the world beyond the ordinary with
                    Mamba Hikers — where the toughest mountains become the most
                    rewarding milestones on your hiking journey.
                  </p>
                </div>

                {/* Social Links */}
                <div className="mt-10">
                  <h3 className="text-lg font-bold font-montserrat text-mamba-navy mb-4">Connect With Us</h3>
                  <div className="flex gap-4">
                    <motion.a
                      whileHover={{ scale: 1.1, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      href="https://www.instagram.com/mambahikers?igsh=MWNwZXQweDd6ODdoZg=="
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center shadow-lg hover:shadow-xl transition-shadow"
                    >
                      <IoLogoInstagram size={24} color="white" />
                    </motion.a>
                    <motion.a
                      whileHover={{ scale: 1.1, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      href="https://www.facebook.com/mambahikers"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center shadow-lg hover:shadow-xl transition-shadow"
                    >
                      <FaFacebookF size={20} color="white" />
                    </motion.a>
                    <motion.a
                      whileHover={{ scale: 1.1, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      href="https://x.com/Mambahikers?t=zN1p261-8xzi5ERoSEFNoA&s=09"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 rounded-full bg-sky-500 flex items-center justify-center shadow-lg hover:shadow-xl transition-shadow"
                    >
                      <FaTwitter size={20} color="white" />
                    </motion.a>
                  </div>
                </div>
              </motion.div>

              {/* Right: Image with decorative elements */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="relative"
              >
                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src={AboutUsImage}
                    alt="Mamba Hikers Team"
                    className="w-full h-auto object-cover"
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-mamba-navy/40 to-transparent" />
                </div>
                
                {/* Decorative elements */}
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-gradient-to-br from-mamba-golden to-amber-400 rounded-full blur-3xl opacity-50" />
                <div className="absolute -top-6 -left-6 w-32 h-32 bg-gradient-to-br from-mamba-sky-blue to-mamba-navy rounded-full blur-3xl opacity-40" />
              </motion.div>
            </div>
          </div>
        </section>

          {/* <div className="my-28">
            <div className="font-bold text-6xl text-center my-10">
              Begin Your Adventure with Us
            </div>

            <div className="flex space-x-5 flex-wrap space-y-5 justify-center">
              <div className="px-10 py-5 border-[3px]">
                <div className="w-28">
                  <img src={Avatar3} alt="" className="w-full h-auto" />
                </div>

                <div className="font-semibold">John Doe</div>
                <div>Sem eu senectus ridiculus nam fames nisl natoque</div>
              </div>

              <div className="px-10 py-5 border-[3px]">
                <div className="w-28">
                  <img src={Avatar} alt="" className="w-full h-auto" />
                </div>

                <div className="font-semibold">John Doe</div>
                <div>Sem eu senectus ridiculus nam fames nisl natoque</div>
              </div>

              <div className="px-10 py-5 border-[3px]">
                <div className="w-28">
                  <img src={Avatar2} alt="" className="w-full h-auto" />
                </div>

                <div className="font-semibold">John Doe</div>
                <div>Sem eu senectus ridiculus nam fames nisl natoque</div>
              </div>

              <div className="px-10 py-5 border-[3px]">
                <div className="w-28">
                  <img src={Avatar} alt="" className="w-full h-auto" />
                </div>

                <div className="font-semibold">John Doe</div>
                <div>Sem eu senectus ridiculus nam fames nisl natoque</div>
              </div>
            </div>
          </div> */}

        {/* TESTIMONIALS SECTION */}
        <section className="px-5 md:px-10 lg:px-20 xl:px-32 py-16 lg:py-24 bg-white">
          <div className="max-w-8xl mx-auto">
            {/* Section Header */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="h-1 w-12 bg-gradient-to-r from-transparent via-mamba-golden to-transparent rounded-full" />
                <span className="text-xs font-bold font-montserrat uppercase tracking-wider text-mamba-golden">
                  Testimonials
                </span>
                <div className="h-1 w-12 bg-gradient-to-r from-transparent via-mamba-golden to-transparent rounded-full" />
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-black font-montserrat text-mamba-navy">
                What Our <span className="italic font-dancing-script bg-gradient-to-r from-mamba-sky-blue to-mamba-golden bg-clip-text text-transparent">Hikers</span> Say
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Testimonial Card 1 */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                whileHover={{ y: -8 }}
                className="relative bg-gradient-to-br from-white to-mamba-bg-light p-8 rounded-3xl border-2 border-mamba-sky-blue/20 hover:border-mamba-golden shadow-lg hover:shadow-2xl transition-all group"
              >
                <Quote className="absolute top-6 right-6 w-12 h-12 text-mamba-golden/20 group-hover:text-mamba-golden/40 transition-colors" />
                <div className="flex items-center space-x-4 mb-6 relative z-10">
                  <div className="relative">
                    <img
                      src={Avatar}
                      alt="Jane Wanjiku"
                      className="w-16 h-16 rounded-full object-cover ring-4 ring-mamba-sky-blue/20"
                    />
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-mamba-golden rounded-full flex items-center justify-center">
                      <Award className="w-3 h-3 text-white" />
                    </div>
                  </div>
                  <div>
                    <div className="font-bold font-montserrat text-lg text-mamba-navy">Jane Wanjiku</div>
                    <div className="text-sm font-inter text-mamba-navy/60">Adventurer</div>
                  </div>
                </div>
                <p className="text-slate-700 font-inter leading-relaxed relative z-10">
                  "Traveling with this team was the best decision I ever made.
                  Everything was seamless, and I got to explore places I never
                  imagined."
                </p>
              </motion.div>

              {/* Testimonial Card 2 */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                whileHover={{ y: -8 }}
                className="relative bg-gradient-to-br from-white to-mamba-bg-light p-8 rounded-3xl border-2 border-mamba-sky-blue/20 hover:border-mamba-golden shadow-lg hover:shadow-2xl transition-all group"
              >
                <Quote className="absolute top-6 right-6 w-12 h-12 text-mamba-golden/20 group-hover:text-mamba-golden/40 transition-colors" />
                <div className="flex items-center space-x-4 mb-6 relative z-10">
                  <div className="relative">
                    <img
                      src={Avatar2}
                      alt="Ali Omondi"
                      className="w-16 h-16 rounded-full object-cover ring-4 ring-mamba-sky-blue/20"
                    />
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-mamba-golden rounded-full flex items-center justify-center">
                      <Award className="w-3 h-3 text-white" />
                    </div>
                  </div>
                  <div>
                    <div className="font-bold font-montserrat text-lg text-mamba-navy">Ali Omondi</div>
                    <div className="text-sm font-inter text-mamba-navy/60">Backpacker</div>
                  </div>
                </div>
                <p className="text-slate-700 font-inter leading-relaxed relative z-10">
                  "I loved how personal and tailored the experience was.
                  Definitely recommending to my friends."
                </p>
              </motion.div>

              {/* Testimonial Card 3 */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                whileHover={{ y: -8 }}
                className="relative bg-gradient-to-br from-white to-mamba-bg-light p-8 rounded-3xl border-2 border-mamba-sky-blue/20 hover:border-mamba-golden shadow-lg hover:shadow-2xl transition-all group"
              >
                <Quote className="absolute top-6 right-6 w-12 h-12 text-mamba-golden/20 group-hover:text-mamba-golden/40 transition-colors" />
                <div className="flex items-center space-x-4 mb-6 relative z-10">
                  <div className="relative">
                    <img
                      src={Avatar3}
                      alt="Sarah Mutheu"
                      className="w-16 h-16 rounded-full object-cover ring-4 ring-mamba-sky-blue/20"
                    />
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-mamba-golden rounded-full flex items-center justify-center">
                      <Award className="w-3 h-3 text-white" />
                    </div>
                  </div>
                  <div>
                    <div className="font-bold font-montserrat text-lg text-mamba-navy">Sarah Mutheu</div>
                    <div className="text-sm font-inter text-mamba-navy/60">Solo Traveler</div>
                  </div>
                </div>
                <p className="text-slate-700 font-inter leading-relaxed relative z-10">
                  "From planning to the actual trip, they handled everything. I
                  just relaxed and enjoyed. Can't wait for my next adventure!"
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative min-h-[400px] lg:min-h-[500px] bg-call-to-action2 bg-cover bg-center bg-fixed overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-mamba-navy/95 via-slate-900/90 to-mamba-sky-blue/85" />
          
          {/* Floating elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-10">
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute"
                style={{
                  left: `${Math.random() * 90}%`,
                  top: `${Math.random() * 90}%`,
                }}
                animate={{
                  y: [0, -40, 0],
                  rotate: [0, 20, 0],
                }}
                transition={{
                  duration: 5 + i,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <Mountain className="w-20 h-20 text-white" />
              </motion.div>
            ))}
          </div>

          <div className="relative z-10 h-full flex items-center justify-center px-5 py-16">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl w-full text-center"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-mamba-golden/20 backdrop-blur-sm rounded-full border border-mamba-golden/40 mb-6"
              >
                <Users className="w-4 h-4 text-mamba-golden" />
                <span className="text-sm font-bold font-montserrat text-mamba-golden uppercase tracking-wider">Private Events</span>
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-4xl md:text-5xl lg:text-6xl font-black font-montserrat text-white mb-6 leading-tight"
              >
                We Offer <span className="italic font-dancing-script text-mamba-golden">Private</span> Hiking Events
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-lg md:text-xl text-white/90 font-inter mb-10 max-w-2xl mx-auto"
              >
                Looking for a customized adventure? Contact us for tailored experiences designed just for you and your group.
              </motion.p>

              <motion.button
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.5 }}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSendEmail}
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-mamba-golden to-amber-400 text-mamba-navy font-bold font-montserrat rounded-full shadow-2xl hover:shadow-mamba-golden/50 transition-all text-lg"
              >
                <Mail className="w-5 h-5" />
                Contact Us
              </motion.button>
            </motion.div>
          </div>
        </section>

        {/* Location Section */}
        <section className="px-5 md:px-10 lg:px-20 xl:px-32 py-16 lg:py-24 bg-gradient-to-br from-mamba-bg-light via-white to-mamba-bg-light">
          <div className="max-w-8xl mx-auto">
            {/* Section Header */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <div className="flex items-center justify-center gap-3 mb-4">
                <MapPin className="w-6 h-6 text-mamba-sky-blue" />
                <span className="text-xs font-bold font-montserrat uppercase tracking-wider text-mamba-sky-blue">
                  Visit Us
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-black font-montserrat text-mamba-navy mb-4">
                Our <span className="italic font-dancing-script bg-gradient-to-r from-mamba-sky-blue to-mamba-golden bg-clip-text text-transparent">Location</span>
              </h2>
              <p className="text-xl font-inter text-mamba-navy/80">
                J's Arcade, Thome <span className="font-bold">Outlet Number J28</span>
              </p>
            </motion.div>

            {/* Map Container */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative h-[400px] md:h-[500px] rounded-3xl overflow-hidden shadow-2xl border-4 border-mamba-navy/10"
            >
              <iframe
                title="Google Map - J's Arcade"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.900123851412!2d36.870514799999995!3d-1.2292414999999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x182f15c08c4f72e3%3A0xa9a7571c449c0657!2sJ's%20Arcade!5e0!3m2!1sen!2ske!4v1746636715200!5m2!1sen!2ske"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="grayscale-0 hover:grayscale-0 transition-all"
              ></iframe>
            </motion.div>
          </div>
        </section>
      </div>
    </ClientLayout>
  );
};

export default AboutUs;
